import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Users, Plus, Edit, Trash2, Eye, Download } from 'lucide-react';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import Pagination from '../components/Pagination';
import CFiltersBar from '../components/shared/c-filters-bar';
import CTable from '../components/shared/c-table';
import CModal from '../components/shared/c-modal';
import { EMPLOYEE_STATUSES, EMPLOYMENT_TYPES } from '../constants';
import { buildQueryParams } from '../utils/query';
import { getStatusBadgeClasses, formatStatusText } from '../utils/statusUtils';
import { useAuth } from '../contexts/AuthContext';
import usePersistedState from '../hooks/usePersistedState';
import useLatestRequest from '../hooks/useLatestRequest';
import { canEditEmployee } from '../utils/employeePermissions';
import { formatDateOnly } from '../utils/dateUtils';

// The columns of GET /api/employees/bulk-template, in its order. `required` mirrors the
// server: 'every' is checked on every row by the Excel parser (employeeRepo), 'new' only
// when the row creates an employee (NOT NULL columns in createEmployee). Nothing else is
// required, and for an existing employee a blank cell keeps the stored value.
const REQUIRED_LABEL = { every: 'Every row', new: 'New employee' };
const EXACT = 'Exact dropdown value';

const TEMPLATE_COLUMNS = [
  { key: 'first_name', label: 'First Name', required: 'every' },
  { key: 'last_name', label: 'Last Name', required: 'every' },
  {
    key: 'email',
    label: 'Work Email',
    required: 'every',
    note: 'Finds an existing employee. Never changed by an upload',
  },
  { key: 'personal_email', label: 'Personal Email' },
  { key: 'manager_email', label: 'Manager Email', note: "The manager's work email" },
  { key: 'cnic', label: 'CNIC (XXXXX-XXXXXXX-X)' },
  { key: 'phone', label: 'Phone' },
  { key: 'date_of_birth', label: 'Date of Birth (DD-MM-YYYY)' },
  { key: 'gender', label: 'Gender' },
  { key: 'address', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'country', label: 'Country' },
  { key: 'taxable_employee', label: 'Taxable Employee (Yes/No)', note: 'New employee blank = No' },
  { key: 'position', label: 'Position', required: 'new' },
  { key: 'department', label: 'Department', note: EXACT },
  { key: 'hire_date', label: 'Hire Date (DD-MM-YYYY)', required: 'new' },
  {
    key: 'employment_type',
    // \u200b after each slash lets a phone wrap between the options, not mid-word.
    label: `Employment Type (${EMPLOYMENT_TYPES.join('/\u200b')})`,
    note: 'New employee blank = Full-time',
  },
  { key: 'emergency_contact_name', label: 'Emergency Contact Name' },
  { key: 'emergency_contact_phone', label: 'Emergency Contact Phone' },
  {
    key: 'emergency_contact_relationship',
    label: 'Emergency Contact Relationship',
  },
  {
    key: 'salary_amount',
    label: 'Salary Amount',
    note: 'Needs Salary Currency and Payment Method Name',
  },
  { key: 'salary_currency', label: 'Salary Currency (USD/PKR)' },
  {
    key: 'payment_method_name',
    label: 'Payment Method Name',
    note: `${EXACT}. Needed with salary or bank details`,
  },
  { key: 'salary_effective_date', label: 'Salary Effective Date (DD-MM-YYYY)', note: 'Blank = today' },
  { key: 'account_number', label: 'Account Number' },
  { key: 'account_title', label: 'Account Title', note: 'Account holder name' },
  { key: 'bank_name', label: 'Bank Name', note: EXACT },
  {
    key: 'daily_target_hours',
    label: 'Daily Target Hours',
    note: '0 to 24. New employee blank = policy default',
  },
  { key: 'attendance_exempt', label: 'Attendance Exempt (Yes/No)', note: 'New employee blank = No' },
];

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = usePersistedState('employees.search', '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [departmentFilter, setDepartmentFilter] = usePersistedState(
    'employees.department',
    ''
  );
  const [statusFilter, setStatusFilter] = usePersistedState(
    'employees.status',
    'active'
  );
  const [employmentTypeFilter, setEmploymentTypeFilter] = usePersistedState(
    'employees.employmentType',
    ''
  );
  const [departments, setDepartments] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [page, setPage] = usePersistedState('employees.page', 1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const fileInputRef = useRef();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteEmployeeId, setDeleteEmployeeId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const {
    user,
    hasPermission,
    hasDeletePermission,
    hasAnyResourcePermission,
  } = useAuth();
  const canManageLogins = hasPermission('users', 'create');
  // update_all: any row; update_department: someone else in the viewer's department.
  // update_own (every employee) edits no one. The own row is hidden below regardless.
  const canEditRow = (employee) => canEditEmployee(hasPermission, user, employee);

  // Debounce search term to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Only the latest load may fill the list: a page click or a filter change while one
  // is in flight starts another, and a late response would overwrite the newer one.
  // Nor is the response's page written back: it is only ever the page asked for, and
  // writing it back let two loads in flight flip the page between them, refetching on
  // every flip.
  const beginRequest = useLatestRequest();

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
    // eslint-disable-next-line
  }, [
    page,
    limit,
    debouncedSearchTerm,
    departmentFilter,
    statusFilter,
    employmentTypeFilter,
  ]);

  const fetchEmployees = async () => {
    const isCurrent = beginRequest();
    try {
      setSearchLoading(true);

      const params = buildQueryParams({
        page,
        limit,
        search: debouncedSearchTerm,
        department: departmentFilter,
        status: statusFilter,
        employment_type: employmentTypeFilter,
      });

      const response = await api.get('/api/employees', { params });
      if (!isCurrent()) return;
      setEmployees(response.data.data);
      setTotal(response.data.total);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees');
    } finally {
      if (isCurrent()) {
        setLoading(false);
        setSearchLoading(false);
      }
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/api/employees/departments/list');
      setDepartments(response.data.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const totalPages = Math.ceil(total / limit);

  const handleDelete = (id) => {
    setDeleteEmployeeId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/api/employees/${deleteEmployeeId}`);
      toast.success('Employee deleted successfully');
      setShowDeleteModal(false);
      setDeleteEmployeeId(null);
      fetchEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
      toast.error(error.response?.data?.message || 'Failed to delete employee');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (!fileInputRef.current.files[0]) return;
    setBulkUploading(true);
    setBulkResult(null);
    const formData = new FormData();
    formData.append('file', fileInputRef.current.files[0]);
    try {
      const res = await api.post('/api/employees/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBulkResult(res.data);
      fetchEmployees();
    } catch (err) {
      setBulkResult(
        err.response?.data || { success: false, message: 'Bulk upload failed' },
      );
    } finally {
      setBulkUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/api/employees/bulk-template', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employee-upload-template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const columns = [
    {
      key: 'employee',
      header: 'Employee',
      render: (employee) => (
        <div data-testid="employees-cell-name" className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-sm font-medium text-primary-600">
                {employee.first_name[0]}
                {employee.last_name[0]}
              </span>
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {employee.first_name} {employee.last_name}
            </div>
            <div className="text-sm text-gray-500">{employee.email}</div>
            <div className="text-xs text-gray-400">{employee.employee_id}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'position',
      header: 'Position',
      render: (employee) => employee.position,
    },
    {
      key: 'department',
      header: 'Department',
      render: (employee) => employee.department_name,
    },
    {
      key: 'status',
      header: 'Status',
      render: (employee) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClasses(
            employee.employment_status,
            'employee',
          )}`}
        >
          {formatStatusText(employee.employment_status)}
        </span>
      ),
    },
    {
      key: 'employment_type',
      header: 'Employment Type',
      render: (employee) => employee.employment_type || 'Full-time',
    },
    {
      key: 'hire_date',
      header: 'Hire Date',
      render: (employee) =>
        employee.hire_date
          ? formatDateOnly(employee.hire_date)
          : '-',
    },
    ...(canManageLogins
      ? [
          {
            key: 'login',
            header: 'Login',
            render: (employee) =>
              employee.has_user_account ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800">
                  Has Login
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                  No Login
                </span>
              ),
          },
        ]
      : []),
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      cellClassName: 'font-medium',
      render: (employee) => (
        <div className="flex items-center justify-end space-x-2">
          <Link
            to={`/employees/${employee.id}`}
            className="text-primary-600 hover:text-primary-900 p-1"
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </Link>
          {user?.employee_id !== employee.id && (
            <Link
              to={
                canEditRow(employee)
                  ? `/employees/${employee.id}/edit`
                  : '#'
              }
              aria-disabled={!canEditRow(employee)}
              data-testid="employees-edit"
              className={`p-1 ${
                canEditRow(employee)
                  ? 'text-warning-600 hover:text-warning-900 cursor-pointer'
                  : 'text-gray-400 cursor-not-allowed'
              }`}
              title={
                canEditRow(employee)
                  ? 'Edit'
                  : 'You do not have permission to edit employees'
              }
              onClick={(e) => {
                if (!canEditRow(employee)) {
                  e.preventDefault();
                }
              }}
            >
              <Edit className="h-4 w-4" />
            </Link>
          )}
          <button
            onClick={() => {
              if (hasDeletePermission('employees')) {
                handleDelete(employee.id);
              }
            }}
            className={`p-1 ${
              hasDeletePermission('employees')
                ? 'text-danger-600 hover:text-danger-900 cursor-pointer'
                : 'text-gray-400 cursor-not-allowed'
            }`}
            title={
              hasDeletePermission('employees')
                ? 'Delete'
                : 'You do not have permission to delete employees'
            }
            disabled={!hasDeletePermission('employees')}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employees</h1>
          <p className="text-gray-600">Manage your employee database</p>
        </div>
        <div className="flex gap-2">
          {hasPermission('employees', 'create') && (
            <Link
              to="/employees/add"
              data-testid="employees-add-btn"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Employee
            </Link>
          )}
          {hasPermission('employees', 'create') && (
            <button
              className="inline-flex items-center px-4 py-2 border border-primary-600 text-sm font-medium rounded-md shadow-sm text-primary-600 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              onClick={() => {
                setShowBulkModal(true);
                setBulkResult(null);
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              Bulk Upload
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <CFiltersBar
        searchTestId="employees-search"
        clearTestId="employees-clear-filters"
        gridColsClass="md:grid-cols-5"
        showSearch
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search employees..."
        searchLoading={searchLoading}
        filters={[
          {
            key: 'department',
            testId: 'employees-department-filter',
            label: 'Department',
            value: departmentFilter,
            onChange: (e) => setDepartmentFilter(e.target.value),
            options: [
              { value: '', label: 'All Departments' },
              ...departments.map((d) => ({ value: d.name, label: d.name })),
            ],
          },
          {
            key: 'status',
            testId: 'employees-status-filter',
            label: 'Status',
            value: statusFilter,
            onChange: (e) => setStatusFilter(e.target.value),
            options: EMPLOYEE_STATUSES.map((s) => ({
              value: s.value,
              label: s.label,
            })),
          },
          {
            key: 'employmentType',
            label: 'Employment Type',
            value: employmentTypeFilter,
            onChange: (e) => setEmploymentTypeFilter(e.target.value),
            options: [
              { value: '', label: 'All Types' },
              ...EMPLOYMENT_TYPES.map((type) => ({ value: type, label: type })),
            ],
          },
        ]}
        onClear={() => {
          setSearchTerm('');
          setDebouncedSearchTerm('');
          setDepartmentFilter('');
          setStatusFilter('');
          setEmploymentTypeFilter('');
        }}
      />

      {/* Employees List */}
      <CTable
        columns={columns}
        data={employees}
        rowKey="id"
        getRowProps={(employee) => ({
          'data-testid': `employees-row-${employee.id}`,
        })}
        header={
          <h3 className="text-lg font-medium text-gray-900">
            {total} Employee{total !== 1 ? 's' : ''}
          </h3>
        }
        emptyMessage={
          !hasAnyResourcePermission('employees') ? (
            <>
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No access
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                You do not have access to any employee records.
              </p>
            </>
          ) : (
            'No employees found.'
          )
        }
      />

      {/* Pagination Controls */}
      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Bulk Upload Modal */}
      {showBulkModal && (
        <CModal
          onClose={() => setShowBulkModal(false)}
          labelledBy="bulk-upload-title"
          dismissible={!bulkUploading}
          // Its per-row failure report exists nowhere else.
          closeOnBackdrop={false}
          className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto relative"
        >
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
            <button
              type="button"
              aria-label="Close"
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl font-bold"
              onClick={() => setShowBulkModal(false)}
            >
              &times;
            </button>
            <h2 id="bulk-upload-title" className="text-xl font-bold mb-2 pr-8">
              Bulk Employee Upload
            </h2>
            <p className="text-sm text-gray-600">
              Download the Excel template, fill in one employee per row, and
              upload it here.
            </p>
          </div>

          <div className="px-6 py-4">
            <div
              className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
              data-testid="bulk-upload-existing-rule"
            >
              <h4 className="font-semibold text-yellow-900 mb-2">
                Updating an existing employee
              </h4>
              <ul className="text-sm text-yellow-800 space-y-1">
                <li>
                  • A row whose Work Email (or CNIC) matches an employee
                  updates that employee.
                </li>
                <li>
                  • <strong>Leave a cell blank to keep what's stored.</strong>{' '}
                  Only filled cells change anything.
                </li>
                <li>
                  • To clear a value or change a Work Email, edit the
                  employee in the app.
                </li>
              </ul>
            </div>

            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">
                Required columns
              </h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>
                  • <strong>Every row:</strong> First Name, Last Name, Work
                  Email
                </li>
                <li>
                  • <strong>New employee:</strong> also Position and Hire
                  Date. A mistyped Work Email reads as a new employee.
                </li>
                <li>
                  • <strong>Salary Amount</strong> needs Salary Currency and
                  Payment Method Name. Bank details need Payment Method Name.
                </li>
              </ul>
              <h4 className="font-semibold text-blue-900 mt-3 mb-2">
                Names must match exactly
              </h4>
              <p className="text-sm text-blue-800">
                Department, Employment Type, Payment Method Name and Bank Name
                must match the template's dropdown values exactly: 'Meezan
                Bank Limited', not 'Meezan'. Pick them from the dropdown
                rather than typing.
              </p>
              <h4 className="font-semibold text-blue-900 mt-3 mb-2">Dates</h4>
              <p className="text-sm text-blue-800">
                DD-MM-YYYY, YYYY-MM-DD, or an Excel date cell (a formula such
                as =DATE(...) works).
              </p>
              {/* Mirrors the server: the Excel parser (employeeRepo.processExcelUpload)
                  rejects the file on any cell it cannot read, while bulkInsertEmployees
                  fails one row when a name matches nothing or pay columns lack
                  salaries:update_all. */}
              <h4 className="font-semibold text-blue-900 mt-3 mb-2">
                If a cell is wrong
              </h4>
              <ul className="text-sm text-blue-800 space-y-1" data-testid="bulk-upload-failures">
                <li>
                  • <strong>The whole file is rejected</strong> and nothing
                  saves when a cell can't be read, such as a bad date, Yes/No,
                  CNIC, email, Salary Amount or Daily Target Hours, text that
                  is too long, or an Employment Type not in its list.
                </li>
                <li>
                  • <strong>Only that row fails</strong> when a Department,
                  Manager Email, Payment Method or Bank matches nothing, or
                  when it fills salary or bank columns and you don't have the
                  salaries:update_all permission. The other rows still save.
                </li>
              </ul>
            </div>

            <div className="mb-4">
              <button
                type="button"
                onClick={handleDownloadTemplate}
                className="inline-flex items-center text-primary-600 hover:underline text-sm"
              >
                <Download className="h-4 w-4 mr-1" /> Download Template
              </button>
            </div>

            <div className="mb-4">
              <table className="w-full table-fixed sm:table-auto text-xs border mb-2">
                <thead>
                  <tr>
                    <th className="border px-2 py-1 text-left w-[36%] sm:w-auto">Column</th>
                    <th className="border px-2 py-1 text-left w-[26%] sm:w-auto">Required</th>
                    <th className="border px-2 py-1 text-left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {TEMPLATE_COLUMNS.map((col) => (
                    <tr key={col.key}>
                      <td className="border px-2 py-1 break-words">
                        {col.label}
                      </td>
                      <td className="border px-2 py-1 text-red-500">
                        {REQUIRED_LABEL[col.required]}
                      </td>
                      <td className="border px-2 py-1">{col.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <form onSubmit={handleBulkUpload} className="space-y-4">
              <div>
                <label htmlFor="bulk-upload-file" className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Excel File
                </label>
                <input
                  id="bulk-upload-file"
                  type="file"
                  accept=".xlsx,.xls"
                  ref={fileInputRef}
                  className="block w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-primary-600 text-white py-3 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-60 font-medium"
                disabled={bulkUploading}
              >
                {bulkUploading ? 'Uploading...' : 'Upload File'}
              </button>
            </form>

            {bulkResult && (
              <div className="mt-6">
                {bulkResult.success ? (
                  <div className="text-green-700 bg-green-100 rounded p-4 mb-4 text-sm">
                    {bulkResult.summary ? (
                      <span>
                        {bulkResult.summary.success} out of{' '}
                        {bulkResult.summary.total} employees were saved.
                        {bulkResult.summary.failed > 0 && (
                          <span> {bulkResult.summary.failed} failed.</span>
                        )}
                      </span>
                    ) : (
                      <span>Bulk upload successful.</span>
                    )}
                  </div>
                ) : (
                  <div className="text-red-700 bg-red-100 rounded p-4 mb-4 text-sm">
                    {bulkResult.message || 'Bulk upload failed.'}
                  </div>
                )}
                {/* Show detailed error table if errors exist */}
                {bulkResult.errors && bulkResult.errors.length > 0 && (
                  <div className="max-h-60 overflow-y-auto border rounded bg-gray-50 text-xs mt-2">
                    <table className="w-full">
                      <thead className="sticky top-0 bg-gray-100">
                        <tr>
                          <th className="border px-2 py-1 text-left">Row</th>
                          <th className="border px-2 py-1 text-left">
                            Email
                          </th>
                          <th className="border px-2 py-1 text-left">
                            Field
                          </th>
                          <th className="border px-2 py-1 text-left">
                            Status
                          </th>
                          <th className="border px-2 py-1 text-left">
                            Message
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkResult.errors.map((err, idx) => (
                          <tr key={idx}>
                            <td className="border px-2 py-1">{err.row}</td>
                            <td className="border px-2 py-1">{err.email}</td>
                            <td className="border px-2 py-1">{err.field}</td>
                            <td
                              className={`border px-2 py-1 ${err.status === 'success' ? 'text-green-600' : err.status === 'warning' ? 'text-yellow-600' : 'text-red-600'}`}
                            >
                              {err.status}
                            </td>
                            <td className="border px-2 py-1">
                              {err.message}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {/* Fallback for old results array */}
                {bulkResult.results &&
                  bulkResult.results.length > 0 &&
                  !bulkResult.errors && (
                    <div className="max-h-60 overflow-y-auto border rounded bg-gray-50 text-xs">
                      <table className="w-full">
                        <thead className="sticky top-0 bg-gray-100">
                          <tr>
                            <th className="border px-2 py-1 text-left">
                              Row
                            </th>
                            <th className="border px-2 py-1 text-left">
                              Status
                            </th>
                            <th className="border px-2 py-1 text-left">
                              Message
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkResult.results.map((r, idx) => (
                            <tr key={idx}>
                              <td className="border px-2 py-1">{r.row}</td>
                              <td
                                className={`border px-2 py-1 ${r.status === 'success' ? 'text-green-600' : r.status === 'warning' ? 'text-yellow-600' : 'text-red-600'}`}
                              >
                                {r.status}
                              </td>
                              <td className="border px-2 py-1">
                                {r.message || ''}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
              </div>
            )}
          </div>
        </CModal>
      )}

      {/* Delete Employee Modal */}
      {showDeleteModal && (
        <CModal
          onClose={() => setShowDeleteModal(false)}
          labelledBy="delete-employee-title"
          dismissible={!deleting}
          className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative"
        >
          <button
            aria-label="Close"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
          >
            &times;
          </button>
          <h2 id="delete-employee-title" className="text-xl font-bold mb-4 text-danger-700">
            Delete Employee
          </h2>
          <p className="mb-4 text-gray-700">
            Are you sure you want to delete this employee?{' '}
            <b>This action cannot be undone.</b>
            <br />
            <span className="text-danger-600 font-semibold">
              Deleting this employee will also delete all associated user
              accounts, appraisals, salaries, and report rows.
            </span>
          </p>
          <div className="flex justify-end gap-2">
            <button
              className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 rounded bg-danger-600 text-white hover:bg-danger-700 font-semibold"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </CModal>
      )}
    </div>
  );
};

export default Employees;
